// src/services/payroll/payrollPdfAnalyzerService.ts

/**
 * Service zur Analyse von DATEV Lohn-PDFs (Div. Auswertungen)
 * 
 * Extrahiert:
 * 1. Anzahl Arbeitnehmer aus dem Lohnjournal
 * 2. Vorhandensein von Beitragsnachweisen für den Folgemonat
 * 
 * Verwendet pdfjs-dist für clientseitige PDF-Textextraktion.
 * Optimiert für große PDFs durch selektives Scannen.
 *
 * WICHTIG zur Arbeitnehmeranzahl:
 * DATEV bündelt alle Verarbeitungsläufe einer Periode in einem einzigen
 * LOJO-Dokument. Eine Datei kann deshalb mehrere vollständige Lohnjournale
 * enthalten, jedes mit eigenem Summenblatt und eigenem Verarbeitungs-
 * kennzeichen (VKZ). Es wird deshalb nicht mehr beim ersten Summenblatt
 * abgebrochen, sondern es werden alle gefundenen Journale gesammelt.
 * Welches davon gilt, entscheidet die Datenbankfunktion
 * `waehle_aktuelles_lohnjournal`. Die Regel liegt bewusst an genau einer
 * Stelle, weil dieselbe Extraktion in drei Laufzeiten existiert
 * (Browser, Netlify/Node, Supabase/Deno).
 */

import * as pdfjsLib from 'pdfjs-dist';
import { supabase } from '../../lib/supabaseClient';

// Worker von CDN laden (vermeidet CRA-Bundling-Probleme)
pdfjsLib.GlobalWorkerOptions.workerSrc = 
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Deutsche Monatsnamen für Pattern-Matching
const GERMAN_MONTHS: Record<number, string> = {
  1: 'Januar',
  2: 'Februar',
  3: 'März',
  4: 'April',
  5: 'Mai',
  6: 'Juni',
  7: 'Juli',
  8: 'August',
  9: 'September',
  10: 'Oktober',
  11: 'November',
  12: 'Dezember'
};

// Reverse-Lookup: Monatsname → Nummer
const MONTH_NAME_TO_NUMBER: Record<string, number> = {};
for (const [num, name] of Object.entries(GERMAN_MONTHS)) {
  MONTH_NAME_TO_NUMBER[name.toLowerCase()] = parseInt(num, 10);
}

/**
 * Ein im PDF gefundenes Lohnjournal-Summenblatt.
 * `seite` ist die 1-basierte Seitennummer im PDF, nicht die im Formularkopf
 * gedruckte Seitenzahl (die beginnt bei jedem Verarbeitungslauf neu bei 1).
 */
export interface LohnjournalTreffer {
  seite: number;
  vkz: string | null;
  anzahl: number;
  mitEntgelt: number | null;
}

export interface PayrollPdfAnalysisResult {
  success: boolean;
  error?: string;
  data?: {
    anzahlArbeitnehmer: number | null;
    anzahlArbeitnehmerMitEntgelt: number | null;
    hasNextMonthBeitragsnachweis: boolean;
    nextMonth: number | null;
    nextMonthYear: number | null;
    journale: LohnjournalTreffer[];
  };
}

/**
 * Ergebnis der erweiterten Analyse für manuellen Upload
 * Enthält zusätzlich Beraternummer, Mandantennummer und erkannten Monat/Jahr
 */
export interface PayrollPdfIdentifyResult {
  success: boolean;
  error?: string;
  data?: {
    beraternummer: string;
    mandantennummer: string;
    month: number;
    year: number;
    anzahlArbeitnehmer: number | null;
    anzahlArbeitnehmerMitEntgelt: number | null;
    hasNextMonthBeitragsnachweis: boolean;
    nextMonth: number | null;
    nextMonthYear: number | null;
    journale: LohnjournalTreffer[];
    anzahlJournale: number;
    warnung: string | null;
  };
}

/**
 * Letter-Spacing-Heuristik für DATEV-Formulare wie LOA313
 *
 * Bei diesen PDFs liefert pdf.js den Inhaltsbereich (alles unterhalb der
 * statischen Spaltenüberschriften) als "letter-spaced" Text: jeder logische
 * Wert wird als ein Item geliefert, dessen Zeichen einzeln durch Single-Spaces
 * getrennt sind. Beispiele:
 *   "3 5 2 9 5 8"                   → Beraternummer 352958
 *   "K R O N O S - P e r s o n a l" → Firmenname "KRONOS-Personal"
 *   "A p r i l"                     → Monat "April"
 *   "1 0"                           → Arbeitnehmeranzahl 10
 *
 * Erkennung: Mindestens 3 Items, die strikt aus 3+ Einzelzeichen mit Single-
 * Space-Trennung bestehen, qualifizieren die Seite als letter-spaced.
 * Normalisierung greift dann auf alle Items mit 2+ Einzelzeichen (damit auch
 * 2-stellige Zahlen wie "1 0"/"3 0" korrekt zu "10"/"30" werden).
 *
 * Auf normalen DATEV-Lohnjournalen (z.B. LNA314) feuert die Heuristik nicht:
 * Verhalten dort ist unverändert.
 */
const LETTER_SPACED_INDICATOR = /^\S\s\S(?:\s\S)+$/;
const LETTER_SPACED_NORMALIZE = /^\S\s\S(?:\s\S)*$/;

function isLetterSpacedPage(items: ReadonlyArray<unknown>): boolean {
  let count = 0;
  for (const item of items) {
    if (item && typeof item === 'object' && 'str' in item) {
      const s = (item as { str: string }).str;
      if (LETTER_SPACED_INDICATOR.test(s)) {
        count++;
        if (count >= 3) return true;
      }
    }
  }
  return false;
}

function normalizeLetterSpacedItem(s: string): string {
  if (LETTER_SPACED_NORMALIZE.test(s)) {
    return s.replace(/ /g, '');
  }
  return s;
}

/**
 * Extrahiert Text aus einer PDF-Seite mit Zeilen-Rekonstruktion
 */
async function extractPageText(pdf: pdfjsLib.PDFDocumentProxy, pageNum: number): Promise<string> {
  const page = await pdf.getPage(pageNum);
  const textContent = await page.getTextContent();

  // Pre-Scan: Bei letter-spaced Seiten (z.B. DATEV LOA313) werden Item-Strings
  // vor der Zeilen-Rekonstruktion normalisiert. Normale Seiten bleiben unberührt.
  const letterSpaced = isLetterSpacedPage(textContent.items);

  // Gruppiere Text-Items nach Y-Position zu Zeilen
  const lines: string[] = [];
  let lastY = -1;
  let currentLine = '';

  for (const item of textContent.items) {
    if ('str' in item) {
      const y = (item as { transform: number[] }).transform[5];
      const str = letterSpaced ? normalizeLetterSpacedItem(item.str) : item.str;
      if (lastY !== -1 && Math.abs(lastY - y) > 3) {
        if (currentLine.trim()) {
          lines.push(currentLine.trim());
        }
        currentLine = str;
      } else {
        currentLine += ' ' + str;
      }
      lastY = y;
    }
  }
  if (currentLine.trim()) {
    lines.push(currentLine.trim());
  }

  return lines.join('\n');
}

/**
 * Extrahiert die Anzahl Arbeitnehmer aus dem Lohnjournal-Summenblatt
 * 
 * Sucht nach dem Pattern:
 * "Anzahl Arbeitnehmer"
 * "aus Lohnabrechnung {zahl} davon mit Entgelt {zahl}"
 */
function extractEmployeeCount(text: string): { total: number | null; mitEntgelt: number | null } {
  // Pattern 1: "aus Lohnabrechnung XX davon mit Entgelt YY" (exakt)
  const pattern = /aus\s+Lohnabrechnung\s+(\d+)\s+davon\s+mit\s+Entgelt\s+(\d+)/i;
  const match = text.match(pattern);
  
  if (match) {
    return {
      total: parseInt(match[1], 10),
      mitEntgelt: parseInt(match[2], 10)
    };
  }

  // Pattern 2: Flexibler – erlaubt beliebigen Text zwischen den Schlüsselwörtern
  // Fängt Fälle ab, in denen pdf.js Multi-Column-Text zusammenfügt
  const flexPattern = /Lohnabrechnung\s+(\d{1,4})\s+.*?Entgelt\s+(\d{1,4})/is;
  const flexMatch = text.match(flexPattern);
  
  if (flexMatch) {
    return {
      total: parseInt(flexMatch[1], 10),
      mitEntgelt: parseInt(flexMatch[2], 10)
    };
  }

  return { total: null, mitEntgelt: null };
}

/**
 * Extrahiert das Verarbeitungskennzeichen (VKZ) aus dem Seitenkopf.
 *
 * DATEV druckt das VKZ in der Kopfzeile zwischen Firmenname und Datum:
 *   "992453 OPPM GmbH Merseburg L48 10.09.2026"
 *   "992453 redi-Group GmbH 029 23.01.2026"
 * Zusätzlich wird die Beschriftungsvariante "VKZ: L48" abgedeckt, falls
 * pdf.js Label und Wert in dieselbe Zeile legt.
 *
 * Laut DATEV wird das VKZ bei Wiederholungsabrechnungen erhöht und ordnet
 * jede Auswertung eindeutig einer Verarbeitung zu. Es dient hier als
 * Gegenprobe zur Reihenfolge im Dokument.
 */
function extractVkz(text: string): string | null {
  const kopfzeile = /^\d{4,6}\s+.+\s+([A-Za-z0-9]{2,4})\s+\d{2}\.\d{2}\.\d{4}$/;
  const label = /VKZ\s*:?\s*([A-Za-z0-9]{2,4})\b/i;

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    const kopfMatch = line.match(kopfzeile);
    if (kopfMatch) {
      return kopfMatch[1].toUpperCase();
    }
  }

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    const labelMatch = line.match(label);
    if (labelMatch) {
      return labelMatch[1].toUpperCase();
    }
  }

  return null;
}

/**
 * Prüft ob eine Seite einen DÜ-Protokoll Beitragsnachweis für einen bestimmten Monat enthält
 */
function checkForBeitragsnachweis(text: string, monthName: string, year: number): boolean {
  // Pattern: "DÜ-Protokoll Beitragsnachweis im {Monat} {Jahr}"
  // Auch Varianten: "DÜ-Protokoll Beitragsnachweis im {Monat}{Jahr}" (ohne Leerzeichen)
  const pattern = new RegExp(
    `DÜ-Protokoll\\s+Beitragsnachweis\\s+im\\s+${monthName}\\s+${year}`,
    'i'
  );
  return pattern.test(text);
}

/**
 * Prüft ob eine Seite zum Lohnjournal gehört (nicht Jahreswerte)
 */
function isMonthlyLohnjournal(text: string): boolean {
  // Monats-Lohnjournal hat den Monatsnamen ohne "Jahreswerte"
  const hasLohnjournal = /L\s*o\s*h\s*n\s*j\s*o\s*u\s*r\s*n\s*a\s*l|Lohnjournal/i.test(text);
  const isJahreswerte = /Jahreswerte/i.test(text);
  return hasLohnjournal && !isJahreswerte;
}

/**
 * Sammelt ein Summenblatt einer Seite in die Trefferliste.
 * Doppelte Seitennummern sind ausgeschlossen, weil jede Seite genau einmal
 * gelesen wird.
 */
function sammleJournalTreffer(
  treffer: LohnjournalTreffer[],
  text: string,
  pageNum: number
): void {
  if (!/Anzahl\s+Arbeitnehmer/i.test(text)) return;
  if (!isMonthlyLohnjournal(text)) return;

  const counts = extractEmployeeCount(text);
  if (counts.total === null) return;

  const vkz = extractVkz(text);

  treffer.push({
    seite: pageNum,
    vkz,
    anzahl: counts.total,
    mitEntgelt: counts.mitEntgelt
  });

  console.log(
    `[PayrollPdfAnalyzer] Lohnjournal auf Seite ${pageNum}: ${counts.total} Arbeitnehmer ` +
    `(mit Entgelt: ${counts.mitEntgelt}, VKZ: ${vkz || 'nicht gelesen'})`
  );
}

/**
 * Extrahiert Beraternummer und Mandantennummer aus dem PDF-Text
 * 
 * DATEV-Format im Header: "992453/58872/00002" (Beraternr/Mandantnr/Personalnr)
 * Alternatives Format: "Ber.-Nr.: 992453" / "Mand.-Nr.: 58872"
 */
function extractBeraternummerMandantennummer(text: string): { beraternummer: string; mandantennummer: string } {
  // Pattern 1: Slash-Format "123456/12345/xxxxx" (häufigstes DATEV-Format bei Brutto/Netto)
  const slashPattern = /(\d{4,6})\/(\d{4,5})\/\d{3,5}/;
  const slashMatch = text.match(slashPattern);
  if (slashMatch) {
    return {
      beraternummer: slashMatch[1],
      mandantennummer: slashMatch[2]
    };
  }

  // Pattern 2: Explizite Labels "Ber.-Nr." / "Mand.-Nr." oder "Beraternummer" / "Mandantennummer"
  let beraternummer = '';
  let mandantennummer = '';
  
  const berPattern = /(?:Ber\.?\s*-?\s*Nr\.?|Beraternummer)\s*:?\s*(\d{4,6})/i;
  const berMatch = text.match(berPattern);
  if (berMatch) beraternummer = berMatch[1];

  const mandPattern = /(?:Mand\.?\s*-?\s*Nr\.?|Mandantennummer)\s*:?\s*(\d{4,5})/i;
  const mandMatch = text.match(mandPattern);
  if (mandMatch) mandantennummer = mandMatch[1];

  if (beraternummer && mandantennummer) {
    return { beraternummer, mandantennummer };
  }

  // Pattern 2b: DATEV Lohnjournal LNA314-Format
  // PDF.js extrahiert Beraternummer und Mandantennummer als isolierte Zahlen auf eigenen Zeilen.
  // Erkennungsmerkmal: Die Formular-Beschriftungszeile "Berater:  Mandant:" erscheint ohne Zahlen.
  // Danach: erste 6-stellige Einzelzeile = Beraternummer, erste 4-5-stellige Einzelzeile
  // ohne führende 0 (nach der Beraternummer) = Mandantennummer.
  if (/Berater:\s+Mandant:/i.test(text)) {
    beraternummer = '';
    mandantennummer = '';
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (!beraternummer && /^\d{6}$/.test(trimmed)) {
        beraternummer = trimmed;
      } else if (beraternummer && !mandantennummer && /^[1-9]\d{3,4}$/.test(trimmed)) {
        mandantennummer = trimmed;
      }
      if (beraternummer && mandantennummer) break;
    }
    if (beraternummer && mandantennummer) {
      return { beraternummer, mandantennummer };
    }
  }

  // Pattern 3: Lohnjournal zeilenbasiertes Format
  // Zeile 1: "992453 redi-Group GmbH 029 23.01.2026"  (Beraternr + Firmenname + VKZ + Datum)
  // Zeile 2: "58873 Katzbergstr. 3 a 1"               (Mandantnr + Adresse + Seitennr)
  const lines = text.split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const nextLine = lines[i + 1].trim();

    // Zeile beginnt mit 4-6-stelliger Zahl, gefolgt von Text (Firmenname), gefolgt von Datum (DD.MM.YYYY)
    const berLineMatch = line.match(/^(\d{4,6})\s+\S.*\d{2}\.\d{2}\.\d{4}/);
    if (berLineMatch) {
      // Nächste Zeile beginnt mit 4-5-stelliger Zahl (Mandantennummer)
      const mandLineMatch = nextLine.match(/^(\d{4,5})\s+/);
      if (mandLineMatch) {
        return {
          beraternummer: berLineMatch[1],
          mandantennummer: mandLineMatch[1]
        };
      }
    }
  }

  return { beraternummer, mandantennummer };
}

/**
 * Extrahiert Abrechnungsmonat und Jahr aus dem Lohnjournal-Header
 * 
 * Typisches Pattern: "Lohnjournal Oktober 2025" oder "L o h n j o u r n a l   Oktober  2025"
 * Alternatives Pattern: "Abrechnungsmonat: Oktober 2025" oder "für Oktober 2025"
 */
function extractMonthYear(text: string): { month: number | null; year: number | null } {
  // Baue dynamisches Pattern aus allen deutschen Monatsnamen
  const monthNames = Object.values(GERMAN_MONTHS).join('|');
  
  // Pattern 1: "Lohnjournal {Monat} {Jahr}" (auch mit Leerzeichen im Wort)
  const lohnjournalPattern = new RegExp(
    `(?:L\\s*o\\s*h\\s*n\\s*j\\s*o\\s*u\\s*r\\s*n\\s*a\\s*l|Lohnjournal)\\s+(${monthNames})\\s+(\\d{4})`,
    'i'
  );
  const ljMatch = text.match(lohnjournalPattern);
  if (ljMatch) {
    const monthNum = MONTH_NAME_TO_NUMBER[ljMatch[1].toLowerCase()];
    return { month: monthNum || null, year: parseInt(ljMatch[2], 10) };
  }

  // Pattern 2: "Abrechnungsmonat: {Monat} {Jahr}" oder "für {Monat} {Jahr}"
  const abrMonatPattern = new RegExp(
    `(?:Abrechnungsmonat|für)\\s*:?\\s*(${monthNames})\\s+(\\d{4})`,
    'i'
  );
  const amMatch = text.match(abrMonatPattern);
  if (amMatch) {
    const monthNum = MONTH_NAME_TO_NUMBER[amMatch[1].toLowerCase()];
    return { month: monthNum || null, year: parseInt(amMatch[2], 10) };
  }

  // Pattern 3: "{Monat} {Jahr}" generisch
  // matchAll statt match: iteriere ALLE Treffer und nimm den ersten plausiblen.
  // Hintergrund: Jedes DATEV-Lohnjournal enthält im Footer den Disclaimer
  // "Das Gesamtbrutto enthält bis einschließlich Mai 2013 ... Ab Juni 2013 ..."
  // Mit single match() würde "Mai 2013" als erster Treffer gefunden, der
  // Plausibilitäts-Filter würde 2013 ablehnen und die Funktion gibt null
  // zurück. matchAll erlaubt es, weiterzusuchen und den echten Abrechnungs-
  // monat zu finden (z.B. "April 2026").
  const genericPattern = new RegExp(
    `(${monthNames})\\s+(\\d{4})`,
    'gi'
  );
  const genMatches = Array.from(text.matchAll(genericPattern));
  for (const m of genMatches) {
    const monthNum = MONTH_NAME_TO_NUMBER[m[1].toLowerCase()];
    const year = parseInt(m[2], 10);
    if (monthNum && year >= 2020 && year <= 2100) {
      return { month: monthNum, year };
    }
  }

  return { month: null, year: null };
}

/**
 * Antwort der Datenbankfunktion `waehle_aktuelles_lohnjournal`.
 */
interface JournalAuswahl {
  erfolg: boolean;
  konflikt: boolean;
  anzahl_journale: number;
  fehler?: string;
  warnung?: string | null;
  gewaehlt?: {
    seite: number;
    vkz: string | null;
    anzahl: number;
    mit_entgelt: number | null;
  };
}

/**
 * Ruft die Auswahlregel in der Datenbank auf.
 *
 * Bei genau einem gefundenen Journal ist die Auswahl trivial. Fällt der
 * Aufruf dann aus, wird lokal entschieden, damit ein Netzwerkfehler den
 * Normalfall nicht blockiert. Bei mehreren Journalen ist der Aufruf zwingend,
 * dort wird im Fehlerfall bewusst nichts übernommen.
 */
async function waehleAktuellesJournal(
  journale: LohnjournalTreffer[],
  year: number,
  month: number,
  beraternummer: string,
  mandantennummer: string
): Promise<{ ok: boolean; fehler?: string; anzahl: number | null; mitEntgelt: number | null; warnung: string | null }> {

  if (journale.length === 0) {
    return { ok: true, anzahl: null, mitEntgelt: null, warnung: null };
  }

  const { data, error } = await supabase.rpc('waehle_aktuelles_lohnjournal', {
    p_journale: journale.map(j => ({
      seite: j.seite,
      vkz: j.vkz,
      anzahl: j.anzahl,
      mit_entgelt: j.mitEntgelt
    })),
    p_year: year,
    p_month: month,
    p_beraternummer: beraternummer,
    p_mandantennummer: mandantennummer
  });

  if (error || !data) {
    console.error('[PayrollPdfAnalyzer] Auswahl des aktuellen Lohnjournals fehlgeschlagen:', error);

    if (journale.length === 1) {
      return {
        ok: true,
        anzahl: journale[0].anzahl,
        mitEntgelt: journale[0].mitEntgelt,
        warnung: null
      };
    }

    return {
      ok: false,
      fehler: `Die Datei enthält ${journale.length} Lohnjournale. Welches davon gilt, konnte nicht bestimmt werden, weil die Auswahlregel nicht erreichbar war. Es wurde nichts übernommen.`,
      anzahl: null,
      mitEntgelt: null,
      warnung: null
    };
  }

  const auswahl = data as unknown as JournalAuswahl;

  if (!auswahl.erfolg) {
    if (auswahl.konflikt) {
      return { ok: false, fehler: auswahl.fehler, anzahl: null, mitEntgelt: null, warnung: null };
    }
    return { ok: true, anzahl: null, mitEntgelt: null, warnung: null };
  }

  return {
    ok: true,
    anzahl: auswahl.gewaehlt ? auswahl.gewaehlt.anzahl : null,
    mitEntgelt: auswahl.gewaehlt ? auswahl.gewaehlt.mit_entgelt : null,
    warnung: auswahl.warnung || null
  };
}

/**
 * Hauptfunktion: Analysiert ein DATEV-PDF und extrahiert Abrechnungsdaten
 *
 * Scan-Strategie:
 * - Schrittweite 1, solange noch kein Summenblatt gefunden wurde oder die
 *   aktuelle Seite zum Lohnjournal gehört. Damit werden auch mehrere
 *   aufeinanderfolgende Verarbeitungsläufe vollständig erfasst.
 * - Schrittweite 5, sobald die Lohnjournal-Sektion verlassen ist und nur noch
 *   der Beitragsnachweis des Folgemonats gesucht wird.
 * - Abbruch, sobald Summenblatt und Folgemonats-BN gefunden sind und die
 *   Lohnjournal-Sektion verlassen ist.
 */
export const payrollPdfAnalyzerService = {
  
  analyzePdf: async (
    file: File | Blob,
    finalizedMonth: number,
    finalizedYear: number
  ): Promise<PayrollPdfAnalysisResult> => {
    try {
      const sourceLabel = 'name' in file && typeof (file as File).name === 'string'
        ? (file as File).name
        : '(Blob)';
      console.log('[PayrollPdfAnalyzer] Starte Analyse:', sourceLabel);
      console.log('[PayrollPdfAnalyzer] Finalisierter Monat:', finalizedMonth, '/', finalizedYear);

      // Folgemonat berechnen
      const nextMonth = finalizedMonth === 12 ? 1 : finalizedMonth + 1;
      const nextMonthYear = finalizedMonth === 12 ? finalizedYear + 1 : finalizedYear;
      const nextMonthName = GERMAN_MONTHS[nextMonth];

      // PDF laden
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
      const totalPages = pdf.numPages;

      console.log(`[PayrollPdfAnalyzer] PDF geladen: ${totalPages} Seiten`);

      const journale: LohnjournalTreffer[] = [];
      let hasNextMonthBN = false;

      let pageNum = 1;
      while (pageNum <= totalPages) {
        const text = await extractPageText(pdf, pageNum);
        const istJournalSeite = isMonthlyLohnjournal(text);

        sammleJournalTreffer(journale, text, pageNum);

        if (!hasNextMonthBN && checkForBeitragsnachweis(text, nextMonthName, nextMonthYear)) {
          hasNextMonthBN = true;
          console.log(`[PayrollPdfAnalyzer] Folgemonats-BN gefunden auf Seite ${pageNum}`);
        }

        // Abbruch erst, wenn die Lohnjournal-Sektion verlassen ist. Sonst
        // würden weitere Verarbeitungsläufe derselben Periode übersehen.
        if (journale.length > 0 && hasNextMonthBN && !istJournalSeite) {
          console.log('[PayrollPdfAnalyzer] Beide Informationen gefunden, beende Scan');
          break;
        }

        pageNum += (journale.length > 0 && !istJournalSeite) ? 5 : 1;
      }

      // Es gilt der Wert aus dem letzten Journal im Dokument. Diese Funktion
      // bedient den Finalisierungspfad und hat weder Kunde noch Berater-
      // nummer zur Hand, deshalb hier ohne Datenbankabgleich.
      const letztes = journale.length > 0 ? journale[journale.length - 1] : null;

      if (journale.length > 1) {
        console.warn(
          `[PayrollPdfAnalyzer] ${journale.length} Lohnjournale gefunden. ` +
          `Übernommen wird das letzte (Seite ${letztes?.seite}, ${letztes?.anzahl} Arbeitnehmer).`
        );
      }

      console.log('[PayrollPdfAnalyzer] Analyse abgeschlossen:', {
        anzahlArbeitnehmer: letztes ? letztes.anzahl : null,
        anzahlMitEntgelt: letztes ? letztes.mitEntgelt : null,
        anzahlJournale: journale.length,
        hasNextMonthBN,
        nextMonth: hasNextMonthBN ? nextMonth : null,
        nextMonthYear: hasNextMonthBN ? nextMonthYear : null
      });

      return {
        success: true,
        data: {
          anzahlArbeitnehmer: letztes ? letztes.anzahl : null,
          anzahlArbeitnehmerMitEntgelt: letztes ? letztes.mitEntgelt : null,
          hasNextMonthBeitragsnachweis: hasNextMonthBN,
          nextMonth: hasNextMonthBN ? nextMonth : null,
          nextMonthYear: hasNextMonthBN ? nextMonthYear : null,
          journale
        }
      };

    } catch (error) {
      console.error('[PayrollPdfAnalyzer] Fehler bei PDF-Analyse:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der PDF-Analyse'
      };
    }
  },

  /**
   * Erweiterte Analyse: Identifiziert Kunde + Monat/Jahr automatisch aus dem PDF
   * 
   * Für den manuellen Lohnjournal-Upload im Rechnungs-Dashboard und für die
   * Übernahme aus dem DATEV-Dokumentenspiegel.
   *
   * Das Dokument wird vollständig gelesen. Die Dateien sind Lohnjournale und
   * damit klein, und nur ein vollständiger Durchlauf findet alle
   * Verarbeitungsläufe einer Periode.
   */
  analyzeAndIdentify: async (file: File): Promise<PayrollPdfIdentifyResult> => {
    try {
      console.log('[PayrollPdfAnalyzer] Starte Identifikations-Analyse:', file.name);

      // PDF laden
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
      const totalPages = pdf.numPages;

      console.log(`[PayrollPdfAnalyzer] PDF geladen: ${totalPages} Seiten`);

      let beraternummer = '';
      let mandantennummer = '';
      let detectedMonth: number | null = null;
      let detectedYear: number | null = null;

      const journale: LohnjournalTreffer[] = [];
      const scannedTexts: string[] = [];
      const TEXT_PUFFER_SEITEN = 30;

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const text = await extractPageText(pdf, pageNum);
        if (pageNum <= TEXT_PUFFER_SEITEN) {
          scannedTexts.push(text);
        }

        // Beraternummer + Mandantennummer (typischerweise auf jeder Seite im Header)
        if (!beraternummer || !mandantennummer) {
          const ids = extractBeraternummerMandantennummer(text);
          if (ids.beraternummer && ids.mandantennummer) {
            beraternummer = ids.beraternummer;
            mandantennummer = ids.mandantennummer;
            console.log(`[PayrollPdfAnalyzer] Identifikation auf Seite ${pageNum}: Ber.${beraternummer} / Mand.${mandantennummer}`);
          }
        }

        // Monat/Jahr aus Lohnjournal-Header
        if (detectedMonth === null && isMonthlyLohnjournal(text)) {
          const monthYear = extractMonthYear(text);
          if (monthYear.month && monthYear.year) {
            detectedMonth = monthYear.month;
            detectedYear = monthYear.year;
            console.log(`[PayrollPdfAnalyzer] Abrechnungszeitraum auf Seite ${pageNum}: ${GERMAN_MONTHS[detectedMonth]} ${detectedYear}`);
          }
        }

        // Monat/Jahr Fallback: Auch aus Nicht-Lohnjournal-Seiten extrahieren (erste 5 Seiten)
        if (detectedMonth === null && pageNum <= 5) {
          const monthYear = extractMonthYear(text);
          if (monthYear.month && monthYear.year) {
            detectedMonth = monthYear.month;
            detectedYear = monthYear.year;
            console.log(`[PayrollPdfAnalyzer] Abrechnungszeitraum (Fallback) auf Seite ${pageNum}: ${GERMAN_MONTHS[detectedMonth]} ${detectedYear}`);
          }
        }

        // Summenblätter sammeln. Kein früher Abbruch: DATEV bündelt mehrere
        // Verarbeitungsläufe in einer Datei, der erste Treffer ist deshalb
        // nicht zwangsläufig der gültige.
        sammleJournalTreffer(journale, text, pageNum);
      }

      // Validierung
      if (!beraternummer || !mandantennummer) {
        return {
          success: false,
          error: 'Beraternummer und/oder Mandantennummer konnten nicht aus dem PDF extrahiert werden. Bitte prüfen Sie, ob es sich um ein DATEV-Lohnjournal handelt.'
        };
      }

      if (!detectedMonth || !detectedYear) {
        return {
          success: false,
          error: 'Der Abrechnungsmonat konnte nicht aus dem PDF extrahiert werden. Bitte prüfen Sie, ob es sich um ein DATEV-Lohnjournal handelt.'
        };
      }

      // Folgemonats-BN-Check
      let hasNextMonthBN = false;
      const nextMonth = detectedMonth === 12 ? 1 : detectedMonth + 1;
      const nextMonthYear = detectedMonth === 12 ? detectedYear + 1 : detectedYear;
      const nextMonthName = GERMAN_MONTHS[nextMonth];

      for (let i = 0; i < scannedTexts.length; i++) {
        if (checkForBeitragsnachweis(scannedTexts[i], nextMonthName, nextMonthYear)) {
          hasNextMonthBN = true;
          console.log(`[PayrollPdfAnalyzer] Folgemonats-BN gefunden auf Seite ${i + 1}`);
          break;
        }
      }

      // Auswahl des gültigen Journals über die zentrale Regel in der Datenbank
      const auswahl = await waehleAktuellesJournal(
        journale,
        detectedYear,
        detectedMonth,
        beraternummer,
        mandantennummer
      );

      if (!auswahl.ok) {
        return {
          success: false,
          error: auswahl.fehler || 'Das gültige Lohnjournal konnte nicht bestimmt werden.'
        };
      }

      console.log('[PayrollPdfAnalyzer] Identifikations-Analyse abgeschlossen:', {
        beraternummer, mandantennummer,
        month: detectedMonth, year: detectedYear,
        anzahlJournale: journale.length,
        anzahlArbeitnehmer: auswahl.anzahl,
        anzahlMitEntgelt: auswahl.mitEntgelt,
        hasNextMonthBN
      });

      return {
        success: true,
        data: {
          beraternummer,
          mandantennummer,
          month: detectedMonth,
          year: detectedYear,
          anzahlArbeitnehmer: auswahl.anzahl,
          anzahlArbeitnehmerMitEntgelt: auswahl.mitEntgelt,
          hasNextMonthBeitragsnachweis: hasNextMonthBN,
          nextMonth: hasNextMonthBN ? nextMonth : null,
          nextMonthYear: hasNextMonthBN ? nextMonthYear : null,
          journale,
          anzahlJournale: journale.length,
          warnung: auswahl.warnung
        }
      };

    } catch (error) {
      console.error('[PayrollPdfAnalyzer] Fehler bei Identifikations-Analyse:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der PDF-Analyse'
      };
    }
  },

  /**
   * Blob-Variante von analyzePdf.
   *
   * Wird für die direkte Analyse von LOJO-PDFs aus dem DATEV-Spiegel (Supabase
   * Storage) verwendet, ohne dass ein Benutzer das PDF uploaden muss. In Stufe 2
   * primär als API-Vorbereitung für Stufe 3/4 - die Backend-Extraktion im
   * invoiceAutofillHandler läuft über eine separate Node-Implementation
   * (payrollLojoExtractor.js).
   */
  analyzeBlob: async (
    blob: Blob,
    finalizedMonth: number,
    finalizedYear: number
  ): Promise<PayrollPdfAnalysisResult> => {
    return payrollPdfAnalyzerService.analyzePdf(blob, finalizedMonth, finalizedYear);
  }
};
